import * as React from 'react';
import { useEffect, useState } from 'react';
import SetPassword from '../../components/registration/set-password';
import { ISetPasswordFormData, ISetPasswordContent } from '../../components/registration/set-password';
import { getUrlParameter } from '@enginuity-common/utils/url';
import { IExchangeActivateTokenResponse } from '../../../../../common/types/user';
import { UserService } from '@enginuity-common/services';
import GenericErrorMessage from '../../components/misc/generic-error-message';
import RequestPasswordResetForm, { IRequestPasswordResetFormData } from '../../components/registration/request-password-reset-form';
import { Spinner } from '@enginuity-apps-ui-components/atoms';
import CheckEmail from '../../components/misc/check-email';
import ConfirmPasswordChange from '../account/confirm-password-change';
import { SpinnerOverlay } from '@enginuity-apps-ui-components/atoms/spinner/spinner';
import { AuthService } from '@enginuity-common/services/authService';
import { setCookie } from '@enginuity-common/utils/cookies';
import { SettingsForApp } from '../../apps';

enum NewPasswordState {
  loading,
  setPassword,
  invalidToken,
  resetPasswordSent,
  setPasswordComplete
};

export type ISetNewPasswordProps = {
  setPasswordContent?: ISetPasswordContent;
  isActivation?: boolean;
  fromUri: string;
  appName: string;
  settingsForApp: SettingsForApp;
};

export default function SetNewPassword({ setPasswordContent, isActivation = true, fromUri, appName, settingsForApp }: ISetNewPasswordProps) {
  const [state, setState] = useState(NewPasswordState.loading);
  const [showSpinner, setShowSpinner] = useState(false);
  const [genericError, setGenericError] = useState<string | undefined>();
  const [passwordErrors, setPasswordErrors] = useState<any>();
  const [resetFormData, setResetFormData] = useState<IRequestPasswordResetFormData>();
  const [activationToken, setActivationToken] = useState(
    getUrlParameter('activationToken')
  );
  const [redirectUrl, setRedirectUrl] = useState(
    getUrlParameter('redirectUrl') || window.spConfig.postActivationDestination as string
  );
  const [stateToken, setStateToken] = useState<string>();

  const [username, setUsername] = useState<string>();

  const userService = new UserService({
    baseUrl: window.spConfig.usersBaseUrl
  });

  const authService = new AuthService({
    baseUrl: window.spConfig.authBaseUrl,
    clientId: window.spConfig.authClientId
  });

  const exchangeActivationToken = async () => {
    setShowSpinner(true);
    setState(NewPasswordState.loading);
    if (!passwordErrors && activationToken) {
      try {
        const response = await userService.exchangeActivationTokenForState(activationToken);
        setShowSpinner(false);
        if (!response.ok) {
          switch (response.status) {
            case 401:
              setState(NewPasswordState.invalidToken);
              return;

            default:
              throw new Error('Unsuccessful response from token exchange.')
          }
        }

        const tokenData: IExchangeActivateTokenResponse = await response.json();
        if (tokenData) {
          setStateToken(tokenData.stateToken);
          setUsername(tokenData.embedded.user.profile.login);
          setState(NewPasswordState.setPassword);
        }
      } catch (err) {
        setShowSpinner(false);
        setGenericError(err.message)
      }
    }
  };

  useEffect(() => {
    exchangeActivationToken();
  }, []);


  const submitForm = async (data: ISetPasswordFormData) => {
    setShowSpinner(true);
    if (stateToken && data.password) {
      userService.setPassword({ stateToken: stateToken, password: data.password })
        .then(response => {
          setShowSpinner(false);

          if (!response.ok) {
            switch (response.status) {
              case 401:
                setState(NewPasswordState.invalidToken);
                return;
              case 403:
                response.text().then(data => {
                  setPasswordErrors(data);
                })


              default:
                throw new Error('Unsuccessful response when setting password.')
            }
          } else {

            if (isActivation) {
              setCookie('enginuity-auth-origin', redirectUrl, 1);
              response.json().then((body: any) => {
                authService.signIn(false, undefined, body.sessionToken);
              });
            }

            setState(NewPasswordState.setPasswordComplete);
          }
        })
        .catch(error => {
          setShowSpinner(false);
          setPasswordErrors(error.message);
        });
    }
  };

  const submitResetForm = async (data: IRequestPasswordResetFormData) => {
    setShowSpinner(true);
    setResetFormData(data);
    try {
      const response = await userService.requestPasswordReset({
        username: data.username,
        appName,
        redirectUrl: redirectUrl || undefined,
        isActivation: isActivation
      });
      setShowSpinner(false);
      if (response.ok) {
        setState(NewPasswordState.resetPasswordSent);
      } else {
        setGenericError('Unsuccessful response when sending password reset email.');
      }
    } catch (err) {
      setShowSpinner(false);
      setGenericError(err.message)
    }
  };

  if (genericError) {
    return <GenericErrorMessage errorMsg={genericError} fromUri={fromUri}></GenericErrorMessage>
  }

  switch (state) {
    case NewPasswordState.loading:
      return (
        <div><Spinner active={true} /></div>
      );

    case NewPasswordState.setPassword:
      return (<>
        <SetPassword onSubmit={submitForm} username={username as string} apiErrors={passwordErrors} setPasswordContent={setPasswordContent}></SetPassword>
        <SpinnerOverlay active={showSpinner} />
      </>
      );

    case NewPasswordState.invalidToken:
      return (
        <div className="container  ">
          <div className="row md-spacer-80 spacer-32">
            <div className="col-lg-1"></div>
            <div className="col-lg-11">
              <h1>Verify email</h1>
            </div>
          </div>
          <div className="row spacer-64">
            <div className="col-lg-2"></div>
            <div className="col-lg-10">
              <p>Sorry, this link is no longer valid. Request a new one by entering your email below.</p>
            </div>
          </div>
          <div className="row spacer-32">
            <div className="col-lg-2"></div>
            <div className="col-lg-4 col-md-6">
              <RequestPasswordResetForm settingsForApp={settingsForApp} onSubmit={submitResetForm} />
            </div>
          </div>
          <SpinnerOverlay active={showSpinner} />
        </div>
      );

    case NewPasswordState.resetPasswordSent:
      return (
        <CheckEmail fromUri={fromUri}><p>Click the link in the email to setup your password</p></CheckEmail>
      );

    case NewPasswordState.setPasswordComplete:
      return (
        <ConfirmPasswordChange />
      );
  }
}

